/**
 * Makes the announcement bar behave differently on either side of the header breakpoint.
 *
 * Desktop has room for both offers at once, so they sit side by side and nothing rotates.
 * Mobile does not — measured at 390px, the two messages joined onto one line run 28% wider
 * than the bar — so there it stays a cross-fade, which is what Horizon's slideshow already
 * does on its own once the arrows are gone.
 *
 * The CSS could show both on desktop by itself, but not correctly: the component marks
 * every slide but the current one `aria-hidden="true"`, and overriding that in CSS alone
 * would leave content that is plainly visible hidden from screen readers. So the attribute
 * is corrected here to match what is actually on screen, and autoplay is stopped rather
 * than left running invisibly behind a static layout.
 */

const DESKTOP = '(min-width: 750px)';

function apply(bar, isDesktop) {
  const slides = bar.querySelectorAll('.announcement-bar__slide');
  if (slides.length < 2) return;

  slides.forEach((slide, index) => {
    slide.setAttribute('aria-hidden', isDesktop ? 'false' : String(index !== 0));
  });

  // `aria-live` announces each rotation. Standing still, it would announce nothing, and a
  // live region that never changes is just noise in the accessibility tree.
  if (isDesktop) {
    bar.removeAttribute('aria-live');
    bar.pause();
    return;
  }

  bar.setAttribute('aria-live', 'polite');

  // Only restart what we stopped. `connectedCallback` already calls `play()`, and each
  // call registers its own `setInterval` — a second one advances the bar twice per tick,
  // which with two slides lands back where it started and reads as frozen.
  if (bar.paused) {
    bar.paused = false;
    bar.play();
  }
}

function init() {
  const bars = document.querySelectorAll('announcement-bar-component');
  if (!bars.length) return;

  const query = window.matchMedia(DESKTOP);
  const sync = () => bars.forEach((bar) => apply(bar, query.matches));

  sync();
  query.addEventListener('change', sync);
}

customElements.whenDefined('announcement-bar-component').then(init);
